import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Layers, Loader2, Sparkles, Plus, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WorkspaceCard } from "@/components/workspace/WorkspaceCard";
import { CreateWorkspaceDialog } from "@/components/workspace/CreateWorkspaceDialog";
import { workspacesApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

export default function Workspaces() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [items, setItems] = useState(null);

    const load = useCallback(async () => {
        try { setItems(await workspacesApi.list()); }
        catch (e) { toast.error(t("workspace.loadFailed")); setItems([]); }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const onCreated = (ws) => setItems((cur) => [...(cur || []).filter((w) => w.is_default), { ...ws }, ...(cur || []).filter((w) => !w.is_default)]);
    const onUpdated = (ws) => setItems((cur) => (cur || []).map((w) => (w.id === ws.id ? { ...w, ...ws } : w)));
    const onDeleted = async (id) => {
        setItems((cur) => (cur || []).filter((w) => w.id !== id));
        try { await workspacesApi.remove(id); toast.success(t("workspace.card.deleted")); load(); }
        catch (e) { toast.error(t("workspace.card.deleteFailed")); load(); }
    };

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10">
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                    <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("workspace.hubTitle")}</h1>
                    <p className="mt-1.5 max-w-xl text-t3">{t("workspace.hubSubtitle")}</p>
                </div>
                <CreateWorkspaceDialog onSaved={onCreated} trigger={
                    <Button data-testid="create-workspace-button" className="gap-2"><Plus className="h-4 w-4" /> {t("workspace.new")}</Button>
                } />
            </div>

            {items === null ? (
                <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
            ) : items.length === 0 ? (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-10 flex flex-col items-center justify-center rounded-3xl border border-dashed border-border bg-surface-1 px-6 py-16 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]"><GraduationCap className="h-7 w-7" /></div>
                    <h3 className="mt-4 font-sans-display text-lg font-semibold text-t1">{t("workspace.emptyTitle")}</h3>
                    <p className="mt-1.5 max-w-md text-sm text-t3">{t("workspace.emptyBody")}</p>
                    <CreateWorkspaceDialog onSaved={onCreated} trigger={<Button className="mt-5 gap-2"><Sparkles className="h-4 w-4" /> {t("workspace.new")}</Button>} />
                </motion.div>
            ) : (
                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((ws, i) => (
                        <WorkspaceCard key={ws.id} ws={ws} index={i} onUpdated={onUpdated} onDeleted={onDeleted} />
                    ))}
                </div>
            )}
        </div>
    );
}
